/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import { lastUpdatedText } from '../date/last-updated'
import { IconClickReload } from '../icon/icon-click'
import { useAlertException } from '../alert-exception/context'
import { wasShown } from '../error/error-notice'

const FormContent = React.createContext()

const useFormContent = () => useContext(FormContent)

export const FormSimple = ({ children, state, disabled, onChange, onSubmit, onRefresh }) => {
	const ref = useRef()

	const alertException = useAlertException()

	const [formData, updateFormData] = useState(null)
	const [dirty, updateDirty] = useState({})
	const [errors, updateErrors] = useState({})
	const [transforms, updateTransforms] = useState({})
	const [submitting, updateSubmitting] = useState(false)

	useEffect(
		() => {
			let newFormData = new FormData()

			if (state) {
				Object.keys(state).forEach((key) => {
					let value = state[key]

					if (typeof value === 'object') {
						value = JSON.stringify(value)
					}

					newFormData.set(key, value)
				})
			}

			updateFormData(newFormData)

			if (state) {
				onChange && onChange(state)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[state]
	)

	const handleGetValue = (name) => {
		return formData ? formData.get(name) : null
	}

	const handleSetValue = (name, value) => {
		let formData = new FormData(ref.current)
		formData.set(name, value)
		updateFormData(formData)
	}

	const handleSetError = (name, error) => {
		updateErrors((errors) => {
			let newValue = { ...errors }
			newValue[name] = error
			return newValue
		})
	}

	const handleSetDirty = (name, isDirty) => {
		updateDirty((dirty) => {
			let newValue = { ...dirty }
			newValue[name] = isDirty
			return newValue
		})
	}

	const handleSetTransform = (name, transform) => {
		updateTransforms((transforms) => {
			let newTransforms = { ...transforms }
			newTransforms[name] = transform
			return newTransforms
		})
	}

	const transformArgs = (values) => {
		Object.keys(transforms).forEach((key) => {
			let value = values[key]
			if (value) {
				values[key] = transforms[key](value)
			}
		})
		return values
	}

	const handleSubmit = async (e) => {
		e.preventDefault()

		updateSubmitting(true)

		if (alertException) {
			alertException.updateException(null)
		}
		
		try {
			let formdata = new FormData(ref.current)
			let values = Object.fromEntries(formdata.entries())
			values = transformArgs(values)
			values = { ...state, ...values }
			onSubmit && (await onSubmit(values))
		} catch (exception) {
			// A failure a notification already showed isn't shown twice.
			if (alertException && !wasShown(exception)) alertException.updateException(exception)
		} finally {
			updateSubmitting(false)
		}
	}

	const handleChange = (e) => {
		let data = new FormData(e.currentTarget)
		let values = Object.fromEntries(data.entries())
		values = transformArgs(values)
		values = { ...state, ...values }
		onChange && onChange(values)
	}

	let lastUpdated = useMemo(() => (state && state.updatedAt ? lastUpdatedText(state.updatedAt) : null), [state])

	const handleChangeForce = () => {
		let data = new FormData(ref.current)
		let values = Object.fromEntries(data.entries())
		values = transformArgs(values)
		values = { ...state, ...values }
		onChange && onChange(values)
	}

	return (
		<FormContent.Provider
			value={{
				formData,
				errors,
				dirty,
				submitting,
				disabled,
				handleSetDirty,
				handleSetTransform,
				handleGetValue,
				handleSetValue,
				handleSetError,
				handleChangeForce
			}}>
			<form ref={ref} onSubmit={handleSubmit} onChange={handleChange}>
				{children}
			</form>
			<div className='d-flex'>
				<small className='flex-fill' />
				{lastUpdated && <small>last updated {lastUpdated}</small>}
				<small>
					{submitting && <span className='mx-1 spinner-border spinner-border-sm' />}
					{!submitting && onRefresh && <IconClickReload onClick={onRefresh} />}
				</small>
			</div>
		</FormContent.Provider>
	)
}

export const useForm = (name, args = {}) => {
	const { formData, errors, dirty, submitting, disabled, handleSetDirty, handleSetTransform, handleSetError, handleChangeForce } = useFormContent()

	const ref = useRef()

	const [original, updateOriginal] = useState('')
	const [value, updateValue] = useState('')

	const getOriginal = () => original
	const getValue = () => value
	const setValue = (value) => {
		updateValue(() => value)
	}

	const handleChange = (e) => {
		updateValue(e.target.value)
	}

	const register = () => {
		return { ref: ref, name: name, value: value, disabled: submitting || disabled, onChange: handleChange }
	}

	useEffect(() => {
		if (args.forceChange) {
			handleChangeForce()
		}
	}, [value])

	const transform = useCallback((value) => {
		if (args.valueAsNumber) {
			let int = parseInt(value)
			if (isNaN(int)) {
				return null
			}
			return int
		}

		if (args.valueAsFloat) {
			let float = parseFloat(value)
			if (isNaN(float)) {
				return null
			}
			return float
		}

		if (args.valueAsBool) {
			if (value.toLowerCase() === 'true') {
				return true
			}

			if (value.toLowerCase() === 'false') {
				return false
			}

			return null
		}

		if (args.valueAsObject) {
			try {
				return JSON.parse(value)
			} catch {
				return {}
			}
		}

		if (args.valueAsFormat) {
			return args.valueAsFormat(value)
		}

		return value
	}, [])

	useEffect(() => {
		handleSetTransform(name, transform)
	}, [])

	useEffect(() => {
		if (formData && name) {
			let value = formData.get(name) || ''
			if (value) {
				updateOriginal(value)
				updateValue(value)
			}
		}
	}, [formData])

	useEffect(() => {
		if (args.checkDirty) {
			let dirty = args.checkDirty(original, value)
			handleSetDirty(name, dirty)
		} else if (args.shouldDirty) {
			handleSetDirty(name, original !== value)
		}
	}, [original, value])

	useEffect(() => {
		if (args.defaultValue) {
			setValue(args.defaultValue)
		}
	}, [])

	const getError = useCallback(() => errors[name], [errors])
	const setError = (error) => handleSetError(name, error)
	const clearError = () => handleSetError(name, null)

	useEffect(() => {
		if (args.required) {
			if (value) {
				clearError()
			} else {
				setError('required')
			}
		}
	}, [value])

	const getFormDirty = useCallback(() => dirty, [dirty])
	const getFormErrors = useCallback(() => errors, [errors])
	const getFormError = useCallback((name) => errors[name], [name, errors])
	const getFormSubmitting = useCallback(() => submitting, [submitting])
	const getFormDisabled = useCallback(() => disabled, [disabled])

	return {
		register,
		getOriginal,
		getValue,
		setValue,
		getError,
		setError,
		clearError,
		getFormErrors,
		getFormError,
		getFormDirty,
		getFormSubmitting,
		getFormDisabled
	}
}

export const checkDirtyObjects = (original, value) => {
	try {
		if (original && value) {
			return !isEqual(JSON.parse(original), JSON.parse(value))
		}
		return false
	} catch {
		return false
	}
}
